import React, { useState, useMemo } from 'react';
import { useSettings } from '../../context/SettingsContext';
import { useVoteData } from '../../context/VoteDataContext';
import aflPlayerData from '../../assets/afl_player_data_2026.json';

// Marks a per-source cell where the player leads that dataset within the game.
const LeaderCheck = ({ className = 'w-2.5 h-2.5 mt-0.5' }) => (
  <svg className={`${className} text-emerald-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
  </svg>
);

const ChevronDown = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
  </svg>
);

const roundChipClass = (isActive) =>
  `min-w-[34px] h-8 px-2 flex items-center justify-center text-[11px] font-mono font-semibold tabular-nums rounded-lg border transition-all flex-shrink-0 ${
    isActive
      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
      : 'text-slate-400 border-slate-700/70 hover:text-slate-200 hover:border-slate-600 hover:bg-slate-800/60'
  }`;

const GameVotesTable = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRound, setSelectedRound] = useState(0);
  const [expandedMobileRows, setExpandedMobileRows] = useState({});
  
  const { data: voteDataRemote } = useVoteData();
  const { getEnabledKeys } = useSettings();
  
  const enabledKeys = getEnabledKeys();
  const enabledSet = new Set(enabledKeys);

  // Function to get player image based on name
  const getPlayerImage = (playerName) => {
    const player = aflPlayerData.find(p => p.player_name === playerName);
    return player ? player.image_src : null;
  };
  
  // Key to label mapping
  const keyToLabel = {
    count1: 'BG 1',
    count2: 'BG 2', 
    count3: 'BG 3',
    count4: 'BG 4',
    count5: 'BG 5',
    betfair: 'BETFAIR',
    espn: 'ESPN',
    byb: 'BYB'
  };

  // Team abbreviations mapping
  const TEAM_ABBREVIATIONS = {
    "Adelaide_Crows": "ADL",
    "Brisbane_Lions": "BL", 
    "Carlton": "CARL",
    "Collingwood": "COLL",
    "Essendon": "ESS",
    "Fremantle": "FRE",
    "Geelong_Cats": "GEEL",
    "Gold_Coast_Suns": "GC",
    "GWS_Giants": "GWS",
    "Hawthorn": "HAW",
    "Melbourne": "MELB",
    "North_Melbourne": "NM",
    "Port_Adelaide": "PORT",
    "Richmond": "RICH",
    "St_Kilda": "STK",
    "Sydney_Swans": "SYD",
    "West_Coast_Eagles": "WCE",
    "Western_Bulldogs": "WBD"
  };

  const getTeamAbbreviation = (teamName) => {
    return TEAM_ABBREVIATIONS[teamName] || teamName;
  };

  const getAbbreviatedGame = (gameString) => {
    // Game format is typically "Team1 - Team2" or "Team1_vs_Team2"
    const parts = gameString.split(' - ');
    if (parts.length === 2) {
      const team1 = getTeamAbbreviation(parts[0].trim());
      const team2 = getTeamAbbreviation(parts[1].trim());
      return `${team1} - ${team2}`;
    }
    return gameString; // Return original if format doesn't match
  };

  const normalize = (value) =>
    (value || '')
      .toString()
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  // Build raw game vote data respecting enabled datasets
  const gameVoteData = useMemo(() => {
    if (!voteDataRemote) return [];
    
    const allGameVotes = [];
    
    // Check if all 8 datasets are enabled
    const allDatasetKeys = ['count1', 'count2', 'count3', 'count4', 'count5', 'betfair', 'espn', 'byb'];
    const allDatasetsEnabled = allDatasetKeys.every(key => enabledSet.has(key));
    
    if (allDatasetsEnabled && voteDataRemote.game_vote_data) {
      // Use pre-calculated game_vote_data when all datasets are enabled
      const gameVoteDataMain = voteDataRemote.game_vote_data;
      
      // Still need to build individual votes breakdown for detailed view
      const datasets = [];
      const pushIf = (key, data, label) => {
        if (data) {
          const wrapped = Array.isArray(data) ? { game_vote_data: data } : data;
          datasets.push({ key, data: wrapped, label });
        }
      };
      
      pushIf('byb', voteDataRemote.game_vote_data_byb, 'BYB');
      pushIf('espn', voteDataRemote.game_vote_data_espn, 'ESPN');
      pushIf('betfair', voteDataRemote.game_vote_data_betfair, 'BETFAIR');
      pushIf('count1', voteDataRemote.game_vote_data_count_1, 'BG 1');
      pushIf('count2', voteDataRemote.game_vote_data_count_2, 'BG 2');
      pushIf('count3', voteDataRemote.game_vote_data_count_3, 'BG 3');
      pushIf('count4', voteDataRemote.game_vote_data_count_4, 'BG 4');
      pushIf('count5', voteDataRemote.game_vote_data_count_5, 'BG 5');
      
      // Build individual votes map
      const individualVotesMap = new Map();
      datasets.forEach(dataset => {
        const data = dataset.data.game_vote_data || [];
        data.forEach(roundData => {
          roundData.game_votes.forEach(game => {
            game.votes.forEach(vote => {
              const key = `${roundData.round}-${game.game}-${vote.player}-${vote.team}`;
              if (!individualVotesMap.has(key)) {
                individualVotesMap.set(key, {});
              }
              individualVotesMap.get(key)[dataset.key] = vote.votes;
            });
          });
        });
      });
      
      // Use main game_vote_data for votes, but add individual breakdown
      gameVoteDataMain.forEach(roundData => {
        roundData.game_votes.forEach(game => {
          game.votes.forEach(vote => {
            const key = `${roundData.round}-${game.game}-${vote.player}-${vote.team}`;
            allGameVotes.push({
              round: roundData.round,
              game: game.game,
              player: vote.player,
              team: vote.team,
              votes: vote.votes,
              individualVotes: individualVotesMap.get(key) || {},
              source: 'main'
            });
          });
        });
      });
    } else {
      // Build from individual datasets when filtering is applied
      const datasets = [];
      const pushIf = (key, data, label) => {
        if (enabledSet.has(key) && data) {
          const wrapped = Array.isArray(data) ? { game_vote_data: data } : data;
          datasets.push({ key, data: wrapped, label });
        }
      };
      
      pushIf('byb', voteDataRemote.game_vote_data_byb, 'BYB');
      pushIf('espn', voteDataRemote.game_vote_data_espn, 'ESPN');
      pushIf('betfair', voteDataRemote.game_vote_data_betfair, 'BETFAIR');
      pushIf('count1', voteDataRemote.game_vote_data_count_1, 'BG 1');
      pushIf('count2', voteDataRemote.game_vote_data_count_2, 'BG 2');
      pushIf('count3', voteDataRemote.game_vote_data_count_3, 'BG 3');
      pushIf('count4', voteDataRemote.game_vote_data_count_4, 'BG 4');
      pushIf('count5', voteDataRemote.game_vote_data_count_5, 'BG 5');
      
      // Build vote map with individual votes breakdown
      const voteMap = new Map();
      
      datasets.forEach(dataset => {
        const data = dataset.data.game_vote_data || [];
        data.forEach(roundData => {
          roundData.game_votes.forEach(game => {
            game.votes.forEach(vote => {
              const key = `${roundData.round}-${game.game}-${vote.player}-${vote.team}`;
              if (!voteMap.has(key)) {
                voteMap.set(key, {
                  round: roundData.round,
                  game: game.game,
                  player: vote.player,
                  team: vote.team,
                  votesSum: 0,
                  count: 0,
                  individualVotes: {}
                });
              }
              const entry = voteMap.get(key);
              entry.votesSum += vote.votes;
              entry.count += 1;
              entry.individualVotes[dataset.key] = vote.votes;
            });
          });
        });
      });
      
      // Convert to final format
      voteMap.forEach(entry => {
        const denominator = enabledKeys.length;
        allGameVotes.push({
          round: entry.round,
          game: entry.game,
          player: entry.player,
          team: entry.team,
          votes: denominator > 0 ? parseFloat((entry.votesSum / denominator).toFixed(2)) : 0,
          individualVotes: entry.individualVotes,
          source: 'computed'
        });
      });
    }
    
    return allGameVotes;
  }, [voteDataRemote, enabledKeys, enabledSet]);

  // Filter data based on search and round selection
  const filteredData = useMemo(() => {
    let filtered = gameVoteData;
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(vote => 
        vote.player.toLowerCase().includes(search) ||
        vote.team.toLowerCase().includes(search) ||
        vote.game.toLowerCase().includes(search)
      );
      // When searching, show results from all rounds
    } else {
      // Only apply round filter when not searching
      const targetRound = parseInt(selectedRound);
      filtered = filtered.filter(vote => {
        const voteRound = typeof vote.round === 'string' ? parseInt(vote.round) : vote.round;
        return voteRound === targetRound;
      });
    }
    
    // Sort data
    const sorted = filtered.sort((a, b) => {
      // Sort by round, then game, then votes (desc)
      if (a.round !== b.round) return a.round - b.round;
      if (a.game !== b.game) return a.game.localeCompare(b.game);
      return b.votes - a.votes;
    });
    
    // Add game index for alternating colors
    let currentGame = null;
    let gameIndex = 0;
    
    return sorted.map(vote => {
      const gameKey = `${vote.round}-${vote.game}`;
      if (currentGame !== gameKey) {
        currentGame = gameKey;
        gameIndex++;
      }
      
      return {
        ...vote,
        gameIndex: gameIndex % 2 // 0 or 1 for alternating colors
      };
    });
  }, [gameVoteData, searchTerm, selectedRound]);

  // Calculate winners for each dataset per game (based on all data, not filtered)
  const gameWinners = useMemo(() => {
    // Don't show winning cells when searching to avoid confusion
    if (searchTerm.trim()) {
      return new Map();
    }
    
    const winners = new Map();
    
    // Group votes by game and dataset using ALL game data (not filtered)
    gameVoteData.forEach(vote => {
      const gameKey = `${vote.round}-${vote.game}`;
      
      if (!winners.has(gameKey)) {
        winners.set(gameKey, {});
      }
      
      const gameWinners = winners.get(gameKey);
      
      // Check each enabled dataset
      enabledKeys.forEach(datasetKey => {
        if (vote.individualVotes && vote.individualVotes[datasetKey] !== undefined) {
          const voteValue = vote.individualVotes[datasetKey];
          
          if (!gameWinners[datasetKey] || voteValue > gameWinners[datasetKey].maxVotes) {
            gameWinners[datasetKey] = {
              maxVotes: voteValue,
              winners: [vote.player]
            };
          } else if (voteValue === gameWinners[datasetKey].maxVotes) {
            if (!gameWinners[datasetKey].winners.includes(vote.player)) {
              gameWinners[datasetKey].winners.push(vote.player);
            }
          }
        }
      });
    });
    
    return winners;
  }, [gameVoteData, enabledKeys, searchTerm]);

  // Check if a player is a winner for a specific dataset in a specific game
  const isWinnerForGame = (round, game, player, datasetKey) => {
    const gameKey = `${round}-${game}`;
    const gameData = gameWinners.get(gameKey);
    
    if (!gameData || !gameData[datasetKey]) return false;
    
    return gameData[datasetKey].winners.includes(player);
  };

  const toggleMobileRow = (key) => {
    setExpandedMobileRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Winning cell class for styling winners
  const leaderCellClass = (isWinner) => (isWinner ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : '');

  // Get unique rounds for filter
  const availableRounds = useMemo(() => {
    const rounds = [...new Set(gameVoteData.map(vote => {
      return typeof vote.round === 'string' ? parseInt(vote.round) : vote.round;
    }))].sort((a, b) => a - b);
    return rounds;
  }, [gameVoteData]);

  if (!voteDataRemote) {
    return (
      <div className="bg-[#0a1120] rounded-2xl border border-slate-800 p-6 text-center">
        <p className="text-slate-400 text-sm">Loading game vote data...</p>
      </div>
    );
  }

  return (
    <>
      {/* Controls and Round Filter Bar.
          Sticks at top-0 (the old top-14 offset assumed the removed navbar)
          and bleeds to the page edges so content scrolls cleanly behind it. */}
      <div className="sticky top-0 z-30 bg-[#020817]/95 backdrop-blur border-b border-slate-800/60 -mx-3 md:-mx-6 px-3 md:px-6 py-2.5 mb-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2.5 lg:gap-4">
          {/* Round Filter Buttons */}
          <div className="min-w-0">
            {/* Mobile: Horizontal scroll */}
            <div className="lg:hidden overflow-x-auto no-scrollbar">
              <div className="flex items-center gap-1.5 pb-0.5" style={{ minWidth: 'max-content' }}>
                {availableRounds.map(round => (
                  <button
                    key={round}
                    onClick={() => {
                      setSelectedRound(round);
                      // Clear search when selecting a round
                      if (searchTerm) {
                        setSearchTerm('');
                      }
                    }}
                    className={roundChipClass(selectedRound === round)}
                  >
                    {round}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Desktop: Flex wrap */}
            <div className="hidden lg:flex lg:items-center lg:gap-3 min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap flex-shrink-0">Round</span>
              <div className="flex flex-wrap items-center gap-1.5">
                {availableRounds.map(round => (
                  <button
                    key={round}
                    onClick={() => {
                      setSelectedRound(round);
                      // Clear search when selecting a round
                      if (searchTerm) {
                        setSearchTerm('');
                      }
                    }}
                    className={roundChipClass(selectedRound === round)}
                  >
                    {round}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Search Input */}
          <div className="relative w-full lg:w-72 flex-shrink-0">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search players, teams, or games..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-slate-900/50 border border-slate-700/70 rounded-lg pl-8 pr-7 h-10 text-xs font-mono text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/60 transition-colors w-full"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-200 transition-colors"
                aria-label="Clear search"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Table */}
      <div className="md:hidden bg-[#0a1120] rounded-2xl border border-slate-800 overflow-hidden">
        <div className="overflow-hidden">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-10" />
              <col className="w-20" />
              <col className="w-[46%]" />
              <col className="w-[24%]" />
            </colgroup>
            <thead>
              <tr className="bg-slate-800/20 border-b border-slate-800/80">
                <th className="w-10 text-center pl-2 pr-1 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">Rnd</th>
                <th className="w-20 text-center px-1 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">Game</th>
                <th className="w-[46%] text-center pl-2 pr-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">Player</th>
                <th className="w-[24%] text-center pl-2 pr-4 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10">Votes</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((vote, index) => {
                const key = `${vote.round}-${vote.game}-${vote.player}-${vote.team}-${index}`;
                const isExpanded = !!expandedMobileRows[key];
                
                return (
                  <React.Fragment key={key}>
                    <tr className={`${vote.gameIndex === 0 ? 'bg-slate-800/[0.18]' : ''} border-b border-slate-800/50`}>
                      <td className="w-10 pl-2 pr-1 py-2 align-top">
                        <span className="text-slate-400 font-mono tabular-nums text-xs">{vote.round}</span>
                      </td>
                      <td className="w-20 px-1 py-2 align-top">
                        <span className="block text-center text-slate-300 text-[10px] font-mono uppercase tracking-[0.04em] leading-tight">{getAbbreviatedGame(vote.game)}</span>
                      </td>
                      <td className="w-[46%] pl-2 pr-2 py-2 align-top">
                        <div className="flex items-center space-x-2 min-w-0">
                          {(() => {
                            const playerImageSrc = getPlayerImage(vote.player);
                            return playerImageSrc ? (
                              <img 
                                src={playerImageSrc} 
                                alt={vote.player}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                <span className="text-white font-bold text-xs">{vote.player.charAt(0)}</span>
                              </div>
                            );
                          })()}
                          <div className="min-w-0 flex-1">
                            <div className="text-white font-semibold text-xs truncate">{vote.player}</div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{getTeamAbbreviation(vote.team)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="w-[24%] pl-2 pr-4 py-2 align-top text-right bg-emerald-500/[0.06]">
                        <div className="inline-flex items-center justify-end gap-2 pr-1">
                          <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm">
                            {vote.votes.toFixed(2)}
                          </span>
                          {vote.individualVotes && (
                            <button
                              className={`w-7 h-7 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-200 ${isExpanded ? 'rotate-180 bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'border-slate-700/60 text-slate-400 hover:text-slate-200 hover:border-slate-600'}`}
                              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                              onClick={() => toggleMobileRow(key)}
                            >
                              <ChevronDown />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded row for detailed view */}
                    {isExpanded && vote.individualVotes && (
                      <tr className="bg-[#060b16] border-b border-slate-800/50">
                        <td colSpan={4} className="px-0 pb-0">
                          <div className="overflow-x-auto">
                            <table className="min-w-[720px] w-full">
                              <thead>
                                <tr className="bg-slate-800/20">
                                  {enabledKeys.map(key => (
                                    <th key={key} className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                      {keyToLabel[key]}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {enabledKeys.map(key => (
                                    <td key={key} className={`text-center px-2 py-2 whitespace-nowrap ${leaderCellClass(isWinnerForGame(vote.round, vote.game, vote.player, key))}`}>
                                      <div className="flex flex-col items-center">
                                        <span className="text-slate-400 font-mono tabular-nums text-xs">
                                          {vote.individualVotes[key]?.toFixed(2) || '0.00'}
                                        </span>
                                        {isWinnerForGame(vote.round, vote.game, vote.player, key) && (
                                          <LeaderCheck />
                                        )}
                                      </div>
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-[#0a1120] rounded-2xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto styled-scrollbar">
          <table className="w-full md:min-w-[800px]">
            <thead>
              <tr className="bg-slate-800/20 border-b border-slate-800/80">
                <th className="text-left px-2 md:px-3 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] w-14">Round</th>
                <th className="text-left px-2 md:px-3 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">Game</th>
                <th className="text-left px-2 md:px-3 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] min-w-[180px]">Player</th>
                {enabledKeys.map((key, index) => (
                  <th key={key} className="hidden md:table-cell text-center px-1 md:px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] select-none">
                    {keyToLabel[key]}
                  </th>
                ))}
                <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] select-none bg-emerald-500/10">Votes</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((vote, index) => {
                const key = `${vote.round}-${vote.game}-${vote.player}-${vote.team}-${index}`;
                
                return (
                  <tr key={key} className={`${vote.gameIndex === 0 ? 'bg-slate-800/[0.18]' : ''} border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors`}>
                    <td className="px-2 md:px-3 py-1.5 w-14">
                      <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">{vote.round}</span>
                    </td>
                    <td className="px-2 md:px-3 py-1.5 whitespace-nowrap">
                      {/* Abbreviated below xl so the full column set fits without side-scrolling */}
                      <span className="text-slate-300 text-[11px] font-mono uppercase tracking-[0.06em] xl:hidden">{getAbbreviatedGame(vote.game)}</span>
                      <span className="hidden xl:inline text-slate-300 text-[11px] font-mono uppercase tracking-[0.06em]">{vote.game.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-2 md:px-3 py-1.5 min-w-[180px]">
                      <div className="flex items-center space-x-2 md:space-x-3">
                        {(() => {
                          const playerImageSrc = getPlayerImage(vote.player);
                          return playerImageSrc ? (
                            <img 
                              src={playerImageSrc} 
                              alt={vote.player}
                              className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                            />
                          ) : (
                            <div className="w-8 h-8 md:w-9 md:h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                              <span className="text-white font-bold text-xs">{vote.player.charAt(0)}</span>
                            </div>
                          );
                        })()}
                        <div className="min-w-0 flex-1">
                          <div className="text-white font-semibold truncate text-xs md:text-[13px]">{vote.player}</div>
                          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{getTeamAbbreviation(vote.team)}</div>
                        </div>
                      </div>
                    </td>
                    {enabledKeys.map((key, index) => (
                      <td key={key} className={`hidden md:table-cell px-1 md:px-2 py-1.5 text-center ${leaderCellClass(isWinnerForGame(vote.round, vote.game, vote.player, key))}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">
                            {vote.individualVotes?.[key]?.toFixed(2) || '0.00'}
                          </span>
                          {isWinnerForGame(vote.round, vote.game, vote.player, key) && (
                            <LeaderCheck />
                          )}
                        </div>
                      </td>
                    ))}
                    <td className="px-2 md:px-4 py-1.5 text-center bg-emerald-500/[0.06]">
                      <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm md:text-[15px]">
                        {vote.votes.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default GameVotesTable;
